import { useEffect, useState } from 'react';
import { DEFAULT_SETTINGS, FONTS, HEIGHTS, type StickerSettings } from '@/entities/price-sticker';

const KEY = 'priceStickers.settings';

// Only a known value is taken back from the browser's memory; anything else falls back to the default.
function read(): StickerSettings {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null') as Partial<StickerSettings> | null;
    if (!v || typeof v !== 'object') return DEFAULT_SETTINGS;
    const num = (x: unknown, d: number) => (typeof x === 'number' && isFinite(x) ? x : d);
    return {
      columns: v.columns === 2 ? 2 : 1,
      height: HEIGHTS.some((h) => h.key === v.height) ? v.height! : DEFAULT_SETTINGS.height,
      font: FONTS.some((f) => f.key === v.font) ? v.font! : DEFAULT_SETTINGS.font,
      autoSize: v.autoSize !== false,
      sizePt: Math.min(200, Math.max(2, num(v.sizePt, DEFAULT_SETTINGS.sizePt))),
      spacingPt: Math.min(20, Math.max(-5, num(v.spacingPt, 0))),
      cutLine: v.cutLine !== false,
    };
  } catch { return DEFAULT_SETTINGS; }
}

// The roll and text settings, remembered in this browser (the sticker text itself is not kept).
export function useStickerSettings() {
  const [s, setS] = useState<StickerSettings>(read);
  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private window: not remembered */ } }, [s]);
  const set = (patch: Partial<StickerSettings>) => setS((old) => ({ ...old, ...patch }));
  const reset = () => setS(DEFAULT_SETTINGS);
  return { s, set, reset };
}
